import React, { useState, useEffect } from 'react';
import { authAxios } from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { User, Palette, Phone, Sparkles } from 'lucide-react';

const PLAYER_COLORS = [
  { value: '#f59e0b', name: 'Amber' },
  { value: '#3b82f6', name: 'Blue' },
  { value: '#10b981', name: 'Emerald' },
  { value: '#ef4444', name: 'Red' },
  { value: '#8b5cf6', name: 'Purple' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#14b8a6', name: 'Teal' },
  { value: '#f97316', name: 'Orange' },
  { value: '#06b6d4', name: 'Cyan' },
  { value: '#84cc16', name: 'Lime' },
  { value: '#a855f7', name: 'Violet' },
  { value: '#64748b', name: 'Slate' },
];

export default function OnboardingModal({ open, user, API, onComplete }) {
  const [displayName, setDisplayName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#3b82f6');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false); // Default to unchecked per Twilio compliance
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && open) {
      // Generate default display name: First Name + First Letter of Last Name
      const nameParts = (user.name || '').split(' ');
      let defaultName = nameParts[0] || '';
      if (nameParts.length > 1 && nameParts[1]) {
        defaultName += ' ' + nameParts[1].charAt(0).toUpperCase() + '.';
      }
      setDisplayName(defaultName || user.name || '');
      setSelectedColor(user.color || '#3b82f6');
    }
  }, [user, open]);

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }

    setSaving(true);
    try {
      const response = await authAxios.post(
        `${API}/users/complete-onboarding`,
        {
          display_name: displayName.trim(),
          color: selectedColor,
          phone_number: phoneNumber.trim() || null,
          sms_enabled: phoneNumber.trim() ? smsOptIn : false
        }
      );
      
      toast.success('Welcome to the league! 🎉');
      onComplete(response.data);
    } catch (error) {
      console.error('Onboarding failed:', error);
      toast.error('Failed to save your preferences');
    } finally {
      setSaving(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-md max-h-[85vh] overflow-y-auto" 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Welcome to the League!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-sm text-slate-600">
            Let's personalize your experience. This only takes a moment!
          </p>

          {/* Display Name */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              Display Name
            </Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How should we call you?"
              className="text-base"
            />
            <p className="text-xs text-slate-500">
              This is how other players will see you on the leaderboard
            </p>
          </div>

          {/* Color Selection */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-500" />
              Your Color
            </Label>
            <div className="grid grid-cols-6 gap-2">
              {PLAYER_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setSelectedColor(color.value)}
                  className={`w-9 h-9 rounded-full transition-all ${
                    selectedColor === color.value 
                      ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' 
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Your color will appear on the leaderboard chart
            </p>
          </div>

          {/* Phone Number */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-500" />
              Phone Number
              <span className="text-xs text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
              placeholder="(555) 123-4567"
              className="text-base"
            />
            
            {/* SMS Opt-in Checkbox - only show when phone number is entered */}
            {phoneNumber.trim() && (
              <div className="mt-2 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="sms-optin"
                    checked={smsOptIn}
                    onCheckedChange={setSmsOptIn}
                    className="mt-0.5"
                  />
                  <label htmlFor="sms-optin" className="text-[11px] text-slate-600 leading-relaxed cursor-pointer">
                    By enabling SMS notifications, you agree to receive text messages from Fantasy Survivor League about gameplay updates such as draft turns, bonus question reminders, and scoring updates. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time.
                  </label>
                </div>
              </div>
            )}
            
            {!phoneNumber.trim() && (
              <p className="text-xs text-slate-500">
                For SMS notifications about drafts and episode reminders
              </p>
            )}
          </div>

          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1.5">Preview</p>
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: selectedColor }}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-slate-800">{displayName || 'Your Name'}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Submit Button - sticky at bottom on mobile */}
          <div className="sticky bottom-0 bg-white pt-2 pb-1">
            <Button 
              onClick={handleSubmit} 
              disabled={saving || !displayName.trim()}
              className="w-full bg-amber-500 hover:bg-amber-600"
              data-testid="onboarding-submit"
            >
              {saving ? 'Saving...' : "Let's Go! 🏝️"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
